// "Export to file" — `onExportCandidatesToCsvClicked` (`extension-alert-candidates.component.ng1.ts:2305`)
// and its dialogue `extension-alert-candidates-csv-export-modal.tpl.html`, shown with
// `useUnifiedTemplate: true, windowClass: "dialog-unified shortlist-dialogue
// extension-candidates-modal-container modal-dialog-slim"`.
//
// ⚠️ CANDIDATES ONLY. The contacts controller's `onExportClientContactsToCsvClicked` and its
// handler registration are commented out upstream, so contacts have no CSV export to port.
//
// The dialogue opens at once and says the download "will begin shortly"; the request runs beside
// it. On failure the dialogue is closed and the "Oops!" alert shown instead, and the rows are not
// flagged.
//
// ⚠️ THE ROWS ARE FLAGGED BY CLOSING THE DIALOGUE, NOT BY THE DOWNLOAD. `onExportCandidateToCsv`
// runs from the body's own ✕ and from `modal.closing` on backdrop click or Escape — and from
// nothing else. The unified header's ✕ is `dismiss('close')`, which neither path sees, so closing
// from there downloads the file and leaves the rows as they were. Upstream's, kept.
import { useEffect, useRef, useState, type ReactElement } from 'react'
import { csvDateString, exportCandidatesToCsv } from '@woo-ext/api'
import { MessageDialog } from '../common/MessageDialog.tsx'
import { UnifiedModal } from '../modal/UnifiedModal.tsx'
import { saveFile } from '../reports/saveFile.ts'
import { CSV_EXPORT_ERROR_MESSAGE, csvModalCloseMarksExported } from './exportToAts.ts'

export interface ExportToCsvModalProps {
  alertId: number
  /** `extensionAlert.name` — the server names the file from it. */
  alertName: string
  /** The checked rows' ids, read once when the dialogue opens. */
  candidateIds: readonly number[]
  /**
   * `onExportCandidateToCsv` is due: flag the rows `exported` and `viewed`, drop them from the
   * list unless the view keeps them, and reset the checkboxes — WITHOUT closing the snippet, which
   * is the one difference from the ATS export's tail.
   */
  onExported: (candidateIds: number[]) => void
  /** Nothing left to do: dismissed (or the failure acknowledged) and the download settled. */
  onClose: () => void
}

export function ExportToCsvModal({
  alertId,
  alertName,
  candidateIds,
  onExported,
  onClose,
}: ExportToCsvModalProps): ReactElement | null {
  const request = useRef({ alertId, alertName, candidateIds: [...candidateIds] })
  const callbacks = useRef({ onExported, onClose })
  useEffect(() => {
    callbacks.current = { onExported, onClose }
  }, [onExported, onClose])

  const [settled, setSettled] = useState(false)
  const [failed, setFailed] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)

  // One download per open, under StrictMode's doubled mount effect too.
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    const { alertId: id, alertName: name, candidateIds: ids } = request.current
    // `utils.getNowStringYYYYMMDDWithSeparator('-')` — the LOCAL date.
    exportCandidatesToCsv(id, name, ids, csvDateString(Date.now()))
      .then((file) => {
        saveFile(file)
      })
      .catch(() => {
        // `modalInstance.close()` then `alerts.error(...)`. `close()` is not a dismissal, so
        // `modal.closing` does not flag the rows.
        setFailed(true)
      })
      .finally(() => {
        setSettled(true)
      })
  }, [])

  useEffect(() => {
    if (settled && (failed ? acknowledged : dismissed)) callbacks.current.onClose()
  }, [settled, failed, acknowledged, dismissed])

  /** `closeModal()`: `ok()`, then `onExportCandidateToCsv()`. */
  const closeModal = (): void => {
    setDismissed(true)
    callbacks.current.onExported([...request.current.candidateIds])
  }

  if (failed) {
    if (acknowledged) return null
    return (
      <MessageDialog
        text={CSV_EXPORT_ERROR_MESSAGE}
        okLabel="Close"
        size="md"
        onOk={() => {
          setAcknowledged(true)
        }}
      />
    )
  }

  return (
    <UnifiedModal
      open={!dismissed}
      windowClass="dialog-unified shortlist-dialogue extension-candidates-modal-container modal-dialog-slim"
      onClose={() => {
        setDismissed(true)
      }}
      onClosing={(action) => {
        if (csvModalCloseMarksExported(action)) callbacks.current.onExported([...request.current.candidateIds])
        return true
      }}
    >
      <div>
        <span className="extension-candidates-modal-text">
          <span>
            <i className="fad fa-check-circle fa-lg extension-candidates-modal-positive" />
          </span>
          <span className="extension-candidates-modal-text-amount">
            <div>
              Great! <br />
              Your download will begin shortly.
            </div>
          </span>
          <i
            className="far fa-times extension-candidates-modal-x cursor-pointer"
            role="button"
            aria-label="Close"
            tabIndex={0}
            onClick={closeModal}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              closeModal()
            }}
          />
        </span>
      </div>
    </UnifiedModal>
  )
}
